import { Link } from "react-router-dom";
import { Lock } from "lucide-react";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#0F172A] text-white">
      <div className="max-w-4xl mx-auto px-4 py-16">
        {/* Header */}
        <div className="mb-12 text-center">
          <div className="w-14 h-14 rounded-2xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mx-auto mb-4">
            <Lock className="w-7 h-7 text-cyan-400" aria-hidden="true" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-2">Privacy Policy</h1>
          <p className="text-[#CBD5E1]">Last updated: May 8, 2026</p>
          <p className="text-[#CBD5E1] text-sm mt-1">NimbleNode | Contact: support@nimblenode.io</p>
        </div>

        <div className="space-y-10 text-[#CBD5E1] leading-relaxed">

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">1. Introduction</h2>
            <p>NimbleNode ("we", "us", "our") is committed to protecting your privacy in compliance with Canada's <em>Personal Information Protection and Electronic Documents Act</em> (PIPEDA) and applicable provincial privacy laws. This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you use our Platform.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">2. Information We Collect</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-base font-medium text-white mb-2">2.1 Information You Provide</h3>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Email address and account credentials</li>
                  <li>Payment information (processed securely by Stripe — we do not store card data)</li>
                  <li>Content you create, upload, or generate on the Platform</li>
                  <li>Communications you send us (support emails, feedback)</li>
                </ul>
              </div>
              <div>
                <h3 className="text-base font-medium text-white mb-2">2.2 Automatically Collected Information</h3>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Usage data (pages visited, features used, credits consumed)</li>
                  <li>Device and browser information</li>
                  <li>IP address and approximate location</li>
                  <li>Session recordings via Microsoft Clarity (mouse movements, clicks, scrolls)</li>
                  <li>Log data and error reports</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">3. Microsoft Clarity Session Recording</h2>
            <p className="p-4 rounded-xl border border-cyan-500/30 bg-cyan-500/5 text-cyan-200">
              We use Microsoft Clarity, a behavioral analytics tool, to understand how users interact with our Platform. Clarity may record session activity including mouse movements, clicks, and scrolling. This data helps us improve the user experience and identify usability issues. Microsoft Clarity data is processed by Microsoft and subject to <a href="https://privacy.microsoft.com/privacystatement" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">Microsoft's Privacy Statement</a>. You may contact us at support@nimblenode.io to opt out.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">4. How We Use Your Information</h2>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>To provide, operate, and improve the Platform</li>
              <li>To process payments and manage your subscription</li>
              <li>To send transactional emails (receipts, account alerts)</li>
              <li>To respond to your support requests</li>
              <li>To analyze usage patterns and improve product features</li>
              <li>To detect and prevent fraud, abuse, or security incidents</li>
              <li>To comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">5. How We Share Your Information</h2>
            <p className="mb-3">We do not sell your personal information. We may share your data with:</p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li><strong className="text-white">Service Providers:</strong> Stripe (payments), Microsoft (Clarity analytics), Ayrshare (social publishing), ElevenLabs (voice synthesis), Google (AI models), HeyGen (AI avatars) — only as needed to provide the service</li>
              <li><strong className="text-white">Legal Authorities:</strong> When required by Canadian law, court order, or to protect the rights and safety of users</li>
              <li><strong className="text-white">Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets, with appropriate notice to you</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">6. Data Retention</h2>
            <p>We retain your personal information for as long as your account is active or as needed to provide services. You may request deletion of your account and associated data at any time. Certain data may be retained for up to 7 years for legal and tax compliance purposes.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">7. Your Rights (PIPEDA)</h2>
            <p className="mb-3">Under PIPEDA, you have the right to:</p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li><strong className="text-white">Access:</strong> Request a copy of the personal information we hold about you</li>
              <li><strong className="text-white">Correction:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong className="text-white">Deletion:</strong> Request deletion of your personal data (subject to legal retention requirements)</li>
              <li><strong className="text-white">Withdrawal of Consent:</strong> Withdraw consent for certain processing activities</li>
              <li><strong className="text-white">Complaint:</strong> Lodge a complaint with the Office of the Privacy Commissioner of Canada (OPC)</li>
            </ul>
            <p className="mt-3">To exercise these rights, contact us at <a href="mailto:support@nimblenode.io" className="text-cyan-400 hover:underline">support@nimblenode.io</a>. We will respond within 30 days.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">8. Data Security</h2>
            <p>We implement industry-standard security measures including encryption in transit (TLS), encrypted storage, access controls, and regular security reviews. However, no method of transmission over the internet is 100% secure. We encourage you to use a strong, unique password and enable two-factor authentication where available.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">9. International Data Transfers</h2>
            <p>Our service providers may process data in countries outside Canada, including the United States. When we transfer data internationally, we take steps to ensure appropriate protections are in place consistent with Canadian privacy law.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">10. Cookies</h2>
            <p>We use cookies and similar tracking technologies to operate the Platform and provide analytics. You can control cookies through your browser settings, though disabling cookies may affect Platform functionality.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">11. Changes to This Policy</h2>
            <p>We may update this Privacy Policy periodically. We will notify you of material changes by email or through the Platform at least 14 days before the changes take effect.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">12. Contact Us</h2>
            <p>For privacy-related questions or to exercise your rights, contact our Privacy Officer at:<br />
              <a href="mailto:support@nimblenode.io" className="text-cyan-400 hover:underline">support@nimblenode.io</a>
            </p>
            <p className="mt-2">You may also contact the Office of the Privacy Commissioner of Canada at <a href="https://www.priv.gc.ca" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline">www.priv.gc.ca</a>.</p>
          </section>
        </div>

        {/* Footer Links */}
        <div className="mt-16 pt-8 border-t border-[#334155] flex flex-wrap gap-4 justify-center text-sm text-[#CBD5E1]">
          <Link to="/" className="hover:text-cyan-400 transition-colors">← Back to Home</Link>
          <Link to="/TermsOfService" className="hover:text-cyan-400 transition-colors">Terms of Service</Link>
          <Link to="/Support" className="hover:text-cyan-400 transition-colors">Support</Link>
        </div>
      </div>
    </div>
  );
}